class ClearCartSubmit extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const form = this.closest("form");
    if (form) {
      const submitButton = form.querySelector('button[type="submit"]');

      // Attach event listener to the submit button
      if (submitButton) {
        submitButton.addEventListener("click", (e) => {
          e.preventDefault();

          fetch("/cart/clear.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          })
            .then((response) => response.json())
            .then(() => {
              form.submit();
            })
            .catch((error) => {
              console.error("Error clearing the cart:", error);
            });
        });
      }
    }
  }
}
customElements.define("clear-cart-submit", ClearCartSubmit);

// compare device
// window.compareDeviceData this variable is set in the liquid file 'compare-device-data-load.liquid'
class CompareDevices extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (window.compareDeviceData.devices.length === 0) {
      console.error("compare device data not available");
      return;
    }

    this.compareDevicesData = window.compareDeviceData.devices;
    this.compareFeaturesData = window.compareDeviceData.features;
    this.compareHeaderEl = this.querySelector("[data-compare-header]");
    this.compareBodyEl = this.querySelector("[data-compare-body]");
    this.compareDeviceSelectorEls = this.querySelectorAll(
      "select[data-device-select]",
    );

    this.initCompareDevices();
  }

  initCompareDevices() {
    this.compareDeviceSelectorEls.forEach((selectEl, index) => {
      selectEl.addEventListener("change", (e) => {
        const selectedDeviceValue = e.target.value;
        this.updateCompareDevice(index, selectedDeviceValue);
      });

      // Initialize with the first device
      const initialDeviceValue = selectEl.value;
      this.updateCompareDevice(index, initialDeviceValue);
    });

    // Remove loader after initialization
    setTimeout(() => {
      const loaderEl = this.parentElement.querySelector(
        "[data-compare-devices-loader]",
      );
      if (loaderEl) {
        loaderEl.remove();
      }

      // Show compare devices element
      this.classList.remove("tw-hidden");
    }, 1000);

    // Handle device resize or orientation change
    window.addEventListener("resize", () => {
      this.updateCompareDeviceBody();
    });

    window.addEventListener("orientationchange", () => {
      this.updateCompareDeviceBody();
    });
  }

  updateCompareDevice(index, deviceName) {
    this.updateCompareDeviceHeader(index, deviceName);
    this.updateCompareDeviceBody();
  }

  updateCompareDeviceHeader(index, deviceName) {
    const deviceData = this.compareDevicesData[deviceName];
    if (!deviceData) {
      console.error(`Device data not found for: ${deviceName}`);
      return;
    }

    // Update header
    const headerBlock = this.compareHeaderEl.querySelector(
      `[data-compare-header-block="${index}"]`,
    );
    const deviceImageEl = headerBlock.querySelector("[data-device-img]");
    const devicePriceEl = headerBlock.querySelector("[data-device-price]");
    const deviceBuyLinkEl = headerBlock.querySelector("[data-device-buy-link]");
    const deviceAvgRatingEl = headerBlock.querySelector(
      "[data-device-rating-avg]",
    );

    deviceBuyLinkEl.href = `/cart/${deviceData.defaultVariantId}:1`;
    deviceAvgRatingEl.textContent = deviceData.rating;
    deviceImageEl.src = deviceData.product_image;
    deviceImageEl.alt = deviceName;
    devicePriceEl.textContent = deviceData.price;

    // update LooxReviewPopup component if exists
    const looxReviewPopupEl = headerBlock.querySelector("loox-review-popup");
    if (looxReviewPopupEl) {
      looxReviewPopupEl.setAttribute("data-product-id", deviceData.productId);
      looxReviewPopupEl.setAttribute("data-product-name", deviceName);
      looxReviewPopupEl.setAttribute(
        "data-product-img",
        deviceData.product_image,
      );
    }
  }

  updateCompareDeviceBody() {
    const deviceData = [];

    // Collect all unique features category from selected devices
    const allFeatureCategories = new Set();

    // If mobile → only use first 2 selectors, else use all
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    const selectorsToProcess = isMobile
      ? Array.from(this.compareDeviceSelectorEls).slice(0, 2)
      : this.compareDeviceSelectorEls;

    selectorsToProcess.forEach((selectEl) => {
      const selectedDeviceName = selectEl.value;
      const selectedDeviceData = this.compareDevicesData[selectedDeviceName];
      if (selectedDeviceData) {
        // store device data for further use
        deviceData.push(selectedDeviceData);
        selectedDeviceData.features.forEach((featureId) => {
          const featureData = this.compareFeaturesData[featureId];
          if (featureData) {
            allFeatureCategories.add(featureData.title);
          }
        });
      }
    });

    // Sort feature categories
    const preferredOrder = [
      "style_type",
      "app_control",
      "bluetooth_streaming",
      "self_fitting",
      "directional_mics",
      "battery_life_time",
      "charge_time",
      "battery_life_with_case",
      "portable_charging_case",
      "uv_cleaning",
      "device_size",
      "visibility_level",
    ];

    const sortedFeatureCategories = [...allFeatureCategories].sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);

      // both are in preferredOrder
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // only a is in preferredOrder
      if (indexA !== -1) return -1;

      // only b is in preferredOrder
      if (indexB !== -1) return 1;

      // neither is in preferredOrder → alphabetical
      return a.localeCompare(b);
    });

    // Build features comparison data
    const featuresComparisonData = sortedFeatureCategories.flatMap(
      (category) => {
        return deviceData.map((device) => {
          const featureId = device.features.find((fid) => {
            const featureData = this.compareFeaturesData[fid];
            return featureData && featureData.title === category;
          });
          const featureData = this.compareFeaturesData[featureId];
          return featureData || null;
        });
      },
    );

    // Build HTML for features comparison
    const featuresHtml = featuresComparisonData
      .map((featureData) => {
        if (featureData) {
          const heading = featureData.heading;
          const name = featureData.name;
          const icon = featureData.icon;

          return /*html*/ `
            <div class="compare-line tw-flex tw-flex-col tw-justify-center tw-py-8 sm:tw-py-6 tw-w-full tw-text-center">
              <div class="tw-max-w-56 tw-px-3 tw-mx-auto tw-relative">
                <div class="tw-flex tw-flex-col tw-gap-2" data-feature-content>
                  ${
                    icon
                      ? `<img
                          src="${icon}"
                          alt="${featureData.title}"
                          class="tw-aspect-square tw-w-6 tw-mx-auto"
                          data-feature-icon
                        />`
                      : ""
                  }

                  ${
                    heading
                      ? `<p class="tw-text-2xl sm:tw-text-xl" data-feature-heading>${heading}</p>`
                      : ""
                  }
                  ${name ? `<p class="tw-text-base" data-feature-name>${name}</p>` : ""}
                </div>
              </div>
            </div>
          `;
        } else {
          return /*html*/ `
                    <div class="compare-line tw-flex tw-flex-col tw-justify-center tw-py-8 tw-w-full tw-text-center">
                      <div class="tw-max-w-56 tw-px-3 tw-mx-auto tw-relative">
                        <div class="tw-flex tw-flex-col tw-gap-2" data-feature-content>
                          <p class="tw-text-2xl" data-feature-heading>-</p>
                        </div>
                      </div>
                    </div>
                  `;
        }
      })
      .join("");

    // Update features in the compare body
    const featuresEl = this.compareBodyEl.querySelector(
      "[data-compare-body-features]",
    );

    // Update features HTML
    featuresEl.innerHTML = featuresHtml;
  }
}
customElements.define("compare-devices", CompareDevices);

// Loox Review popup
class LooxReviewPopup extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.popupEl = this.querySelector("[data-review-popup]");
    this.backdropEl = this.querySelector("[data-popup-backdrop]");
    this.closeButtonEl = this.querySelector("[data-close-popup]");
    this.openButtonEl = this.querySelector("[data-open-popup]");
    this.reviewsContainerEl = this.querySelector("[data-reviews-container]");

    this.initPopupEvents();
  }

  initPopupEvents() {
    if (this.closeButtonEl) {
      this.closeButtonEl.addEventListener("click", () => {
        this.closePopup();
      });
    }

    if (this.backdropEl) {
      this.backdropEl.addEventListener("click", () => {
        this.closePopup();
      });
    }

    if (this.openButtonEl) {
      this.openButtonEl.addEventListener("click", () => {
        this.openPopup();
      });
    }
  }

  closePopup() {
    if (this.popupEl) {
      this.popupEl.classList.add("tw-hidden");
      this.popupEl.classList.remove("tw-flex");

      // clear reviews container
      if (this.reviewsContainerEl) {
        this.reviewsContainerEl.innerHTML = "";
      }
    }
  }

  openPopup() {
    if (this.popupEl) {
      this.popupEl.classList.remove("tw-hidden");
      this.popupEl.classList.add("tw-flex");

      // update name and image
      const productName = this.getAttribute("data-product-name");
      const productImg = this.getAttribute("data-product-img");
      const productId = this.getAttribute("data-product-id");

      const productNameEl = this.querySelector("[data-product-name]");
      const productImgEl = this.querySelector("[data-product-img]");

      if (productName && productNameEl) {
        productNameEl.textContent = productName || "";
      }

      if (productImg && productImgEl) {
        productImgEl.src = productImg || "";
      }

      // load loox reviews
      if (this.reviewsContainerEl) {
        this.reviewsContainerEl.innerHTML = /*html*/ `
          <div class="tw-w-full tw-flex" id="looxReviews" data-product-id="${productId}">
            <div class="tw-w-full tw-flex tw-items-center tw-justify-center tw-m-auto">
              <ui-loader size="24" color="#033FFF"></ui-loader>
            </div>
          </div>
        `;
      }
    }
  }
}
customElements.define("loox-review-popup", LooxReviewPopup);

// Loader
class UILoader extends HTMLElement {
  static get observedAttributes() {
    return ["size", "color"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    this.render();
  }

  render() {
    const size = this.getAttribute("size") || 40;
    const color = this.getAttribute("color") || "#3498db";

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
        }

        .loader {
          width: ${size}px;
          height: ${size}px;
          border: ${size / 8}px solid rgba(0, 0, 0, 0.1);
          border-top-color: ${color};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      </style>

      <div class="loader"></div>
    `;
  }
}

customElements.define("ui-loader", UILoader);
